import {sendEvent} from '@github-ui/hydro-analytics'
import {CommentIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import type {List, Nodes} from 'mdast'
import type {ReactNode} from 'react'
import {use} from 'react'
import {SKIP, visit} from 'unist-util-visit'

import type {CopilotMarkdownExtension, ReactComponentsExtension} from '../../extension'
import {dataAttrToPropName} from '../../utils'
import {ExtensionContext} from '../ExtensionContext'
import styles from './review-comments.module.css'

// Read the review point off the kebab-case prop react-markdown exposes, but set the camelCase hast property name
// (matching the suggestions / issue-link extensions) so hast converts it to the correct DOM attribute.
const REVIEW_COMMENT_ATTRIBUTE = 'data-copilot-review-comment'
const REVIEW_COMMENT_PROPERTY = dataAttrToPropName(REVIEW_COMMENT_ATTRIBUTE)

// The flattened plain text of the item, stored alongside the Markdown so the action's accessible name can use
// readable text (Markdown punctuation like backticks would read awkwardly to a screen reader).
const REVIEW_COMMENT_LABEL_ATTRIBUTE = 'data-copilot-review-comment-label'
const REVIEW_COMMENT_LABEL_PROPERTY = dataAttrToPropName(REVIEW_COMMENT_LABEL_ATTRIBUTE)

/** Matches the invisible marker emitted before a review-comments list, e.g. `<!-- copilot:review-comments -->`. */
const MARKER_REGEX = /^<!--\s*copilot:review-comments\s*-->$/

/** Recursively extract the plain-text content of an mdast node (drops formatting like backticks). */
function mdastToText(node: Nodes): string {
  if ('value' in node && typeof node.value === 'string') return node.value
  if ('children' in node && Array.isArray(node.children)) return node.children.map(mdastToText).join('')
  return ''
}

/**
 * Return the original Markdown source of a list item's content (preserving inline formatting like `inline code`
 * and **bold**) by slicing the source between the first and last child offsets. Falls back to flattened plain text
 * when positional offsets are unavailable (e.g. synthetic nodes without `position`).
 */
function reviewPointMarkdown(item: Nodes, source: string): string {
  if ('children' in item && Array.isArray(item.children) && item.children.length > 0) {
    const start = item.children.at(0)?.position?.start.offset
    const end = item.children.at(-1)?.position?.end.offset
    if (start != null && end != null) {
      const sliced = source.slice(start, end).trim()
      if (sliced) return sliced
    }
  }
  return mdastToText(item).trim()
}

/**
 * Tag each item of a list so it renders with a "draft PR comment" action. Two values are stored: the item's
 * original Markdown (sent to the model so inline formatting survives) and its flattened plain text (used as the
 * action's accessible name, where Markdown punctuation would read awkwardly).
 */
function tagReviewCommentItems(list: List, source: string): void {
  for (const item of list.children) {
    const markdown = reviewPointMarkdown(item, source)
    if (!markdown) continue
    item.data = {
      ...item.data,
      hProperties: {
        ...item.data?.hProperties,
        [REVIEW_COMMENT_PROPERTY]: markdown,
        [REVIEW_COMMENT_LABEL_PROPERTY]: mdastToText(item).trim(),
      },
    }
  }
}

/**
 * Build the short message sent to the model when a review-comment action is activated. It reads naturally as a user
 * turn in the transcript: a one-line instruction followed by the review point as a block quote (its original
 * Markdown — e.g. `inline code` — preserved). The detailed drafting protocol (file path/line number(s) as plain
 * text, comment body inside a single fenced code block, and a trailing "Why this matters" section) lives in the
 * copilot-chat `pr-review-comment` agent ability, which the model loads when it sees this request — so the
 * transcript stays short and the user never sees the verbose instructions.
 *
 * Keep in sync with the backend:
 *   copilot-chat: pkg/agentability/pr_review_comment_ability.go (PrReviewCommentAbility.Body)
 */
function buildReviewCommentPrompt(reviewPoint: string): string {
  // Prefix every line so multi-line review points stay entirely inside the block quote.
  const quoted = reviewPoint
    .split('\n')
    .map(line => `> ${line}`)
    .join('\n')
  return ['Turn this PR review point into a comment.', '', quoted].join('\n')
}

/** Cap the plain-text review point folded into the action's accessible name so it stays readable. */
const MAX_LABEL_LENGTH = 80

// Activating a review item's "draft PR comment" action emits a telemetry event with non-sensitive metadata only
// (the chat mode and the review point's text length). The review point text itself is intentionally omitted:
// capturing customer content requires a restricted-dataset event, which needs additional wiring. Mirrors the
// sibling suggestions extension's `copilot_suggestion_followup_click` event.
const REVIEW_COMMENT_CLICK_EVENT = 'copilot_review_comment_draft_click'

function emitReviewCommentClickTelemetry(reviewPoint: string, mode: 'assistive' | 'immersive' | undefined): void {
  sendEvent(REVIEW_COMMENT_CLICK_EVENT, {mode, reviewPointLength: reviewPoint.length})
}

function ReviewCommentListItem({
  reviewPoint,
  label,
  className,
  children,
}: {
  reviewPoint: string
  label: string
  className?: string
  children?: ReactNode
}) {
  const {onSuggestionSelect, chatMode} = use(ExtensionContext)

  // Without a handler there is nowhere to send the prompt, so render a plain list item.
  if (!onSuggestionSelect) return <li className={className}>{children}</li>

  const handleClick = () => {
    emitReviewCommentClickTelemetry(reviewPoint, chatMode)
    onSuggestionSelect(buildReviewCommentPrompt(reviewPoint), {submit: true})
  }

  // Fold the item's plain text into the accessible name so each action is distinguishable when a list has several
  // (otherwise every button reads identically in a screen reader's element list — WCAG 2.4.6). The visible tooltip
  // stays short via `description`, which Primer renders as the tooltip while keeping `aria-label` as the name.
  const truncated = label.length > MAX_LABEL_LENGTH ? `${label.slice(0, MAX_LABEL_LENGTH)}…` : label
  const accessibleName = truncated ? `Draft a PR comment for: ${truncated}` : 'Draft a PR comment'

  return (
    <li className={clsx(className, styles.reviewCommentItem)}>
      <div className={styles.reviewCommentRow}>
        <div className={styles.reviewCommentContent}>{children}</div>
        <IconButton
          icon={CommentIcon}
          variant="invisible"
          size="small"
          aria-label={accessibleName}
          description="Draft a PR comment"
          className={styles.reviewCommentAction}
          onClick={handleClick}
        />
      </div>
    </li>
  )
}

// Module-level constant so the reference stays stable across renders (required by `useCombinedComponents`).
const reactComponents: ReactComponentsExtension = {
  li: (props, fallthrough) => {
    const reviewPoint = props[REVIEW_COMMENT_ATTRIBUTE]
    if (typeof reviewPoint !== 'string') return fallthrough

    const label = props[REVIEW_COMMENT_LABEL_ATTRIBUTE]
    return (
      <ReviewCommentListItem
        reviewPoint={reviewPoint}
        label={typeof label === 'string' ? label : reviewPoint}
        className={props.className}
      >
        {props.children}
      </ReviewCommentListItem>
    )
  },
}

/**
 * Detects a `<!-- copilot:review-comments -->` marker followed by a list and adds a "draft PR comment" action to
 * each list item. The marker is matched whether the model emits it as a raw HTML comment or wraps/indents it into
 * a code block, so a small instruction-following slip does not silently drop the actions and leak the raw marker.
 * The PR reviewer ability emits the marker before its blocking/risk list and its "Possible
 * Improvements" list; the marker need not be directly adjacent to the list (a short lead-in such as a status line
 * may sit between them), so the scan skips intervening paragraphs and stops at the next section heading. Activating
 * an item's action calls `onSuggestionSelect` from the `ExtensionContext` with a short message asking the model to
 * turn that review point into a PR comment; the detailed drafting protocol (file path and line number(s), a
 * fenced comment body, and a "Why this matters" explanation that helps the reviewer understand the issue) lives in
 * the copilot-chat `pr-review-comment` ability
 * that the model loads in response. The review point is embedded as Markdown so its original formatting (e.g.
 * `inline code`) is preserved. Unlike the suggestions extension, the list keeps its markers and
 * the original item content is left intact — only a trailing icon button is added. The extension is inert when no
 * marker is present or when no `onSuggestionSelect` handler is provided, so it is safe to enable everywhere.
 */
export default function reviewCommentsExtension(): CopilotMarkdownExtension {
  return {
    transformMarkdown: (tree, file) => {
      // The original Markdown source, used to preserve inline formatting (e.g. `inline code`) of each review
      // point by slicing it verbatim via node offsets rather than flattening the mdast to plain text.
      const source = String(file)
      visit(tree, (node, index, parent) => {
        if (index == null || !parent) return
        // The marker is meant to be a raw HTML comment, but models sometimes wrap it in a code fence or indent
        // it, which makes remark parse it as a `code` node instead of `html`. Match both node types so those
        // responses still render the draft-comment actions rather than a plain list with the raw marker leaking
        // out. The regex is anchored, so only a node whose entire content is the marker matches: a marker that
        // appears inside a larger code example is left untouched and rendered as ordinary code.
        if (node.type !== 'html' && node.type !== 'code') return
        if (!MARKER_REGEX.test(node.value.trim())) return

        // Tag the review-comment list that follows the marker. The model is told to put the marker on its own
        // line immediately before the list, but in the risk section it often emits a short lead-in first (e.g. a
        // "**Status**: …" line or a "**Critical Issues to Address**:" heading-style paragraph) and may place the
        // marker right after the section heading. So skip intervening paragraphs and inconsequential
        // whitespace/text/comment nodes when searching for the list. Stop at a heading or thematic break — those
        // begin a new section, so a list beyond them no longer belongs to this marker.
        for (const sibling of parent.children.slice(index + 1)) {
          if (sibling.type === 'list') {
            tagReviewCommentItems(sibling, source)
            break
          }
          if (sibling.type === 'heading' || sibling.type === 'thematicBreak') break
          if (sibling.type !== 'html' && sibling.type !== 'text' && sibling.type !== 'paragraph') break
        }

        // Remove the marker node itself. It is an invisible HTML comment, but react-markdown renders raw HTML as
        // literal text (no rehype-raw in the pipeline), so leaving it in would surface the marker to the user.
        // Stripping it here keeps the marker hidden on every surface, regardless of whether a handler is wired up.
        parent.children.splice(index, 1)
        return [SKIP, index]
      })
    },
    reactComponents,
  }
}
